import { Check, ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { eventCodeKey, STATUS_GROUPS, TRACKING_EVENT_CODES } from '@/lib/tracking-event-codes';
import { cn } from '@/lib/utils';

const triggerClasses =
  'flex h-9 w-full items-center justify-between gap-2 rounded-[10px] border border-[#e5e5e5] bg-white px-3 py-2 text-sm shadow-none';

const LISTBOX_ID = 'detailed-status-listbox';

/**
 * Searchable single-select for the detailed status (carrier event code) filter -
 * 120 options grouped by colour-coding group. Built on the shadcn Command + Popover
 * combobox so the long list is type-to-filter. Search matches the localized title
 * and the raw code; only the title is shown.
 */
export function DetailedStatusCombobox({
  value,
  onChange,
}: {
  value: string | null;
  onChange: (value: string | null) => void;
}) {
  const { t } = useTranslation('shipments');
  const [open, setOpen] = useState(false);

  const titleFor = (code: string) => t(`eventCodes.${eventCodeKey(code)}`, { defaultValue: code });
  const selectedLabel = value ? titleFor(value) : t('filters.anyDetailedStatus');

  const select = (next: string | null) => {
    onChange(next);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-haspopup="listbox"
          aria-controls={LISTBOX_ID}
          aria-expanded={open}
          className={triggerClasses}
        >
          <span className={cn('truncate', value ? 'text-black' : 'text-[#737373]')}>
            {selectedLabel}
          </span>
          <ChevronDown className="size-4 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[--radix-popover-trigger-width] min-w-[18rem] p-0" align="start">
        <Command>
          <CommandInput placeholder={t('filters.detailedStatusSearch')} />
          <CommandList id={LISTBOX_ID}>
            <CommandEmpty>{t('filters.detailedStatusEmpty')}</CommandEmpty>
            <CommandGroup>
              <CommandItem value={t('filters.anyDetailedStatus')} onSelect={() => select(null)}>
                <Check className={cn('mr-2 size-4', value ? 'opacity-0' : 'opacity-100')} />
                {t('filters.anyDetailedStatus')}
              </CommandItem>
            </CommandGroup>
            {STATUS_GROUPS.map((group) => (
              <CommandGroup key={group} heading={t(`statusGroups.${group}`)}>
                {TRACKING_EVENT_CODES.filter((entry) => entry.group === group).map(({ code }) => (
                  <CommandItem
                    key={code}
                    value={`${titleFor(code)} ${code}`}
                    onSelect={() => select(code)}
                  >
                    <Check
                      className={cn('mr-2 size-4', value === code ? 'opacity-100' : 'opacity-0')}
                    />
                    {titleFor(code)}
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
